import React, { useCallback, useMemo } from 'react';
import { Box, Inscription, List } from '@common-fe/common-fe';

import AppButton from '@/components/controls/AppButton';
import { ModalWrapper } from '@/components/wrappers';
import { useGetClaimServicesQuery } from '@/modules/employee/Employee/Documents/queries/useGetClaimServices.query';

import { LMNClaim } from '../LettersOfMedicalNecessity.types';

import LettersOfMedicalNecessityClaimListItem from './LettersOfMedicalNecessityClaimListItem';

interface Props {
  visible: boolean;
  onSetVisible: (visible: boolean) => void;
  claims: LMNClaim[];
  lmnName: string;
}

export const LettersOfMedicalNecessityClaimsModal: React.FC<Props> = ({
  visible,
  onSetVisible,
  claims,
  lmnName,
}) => {
  const serviceIds = useMemo(() => {
    const ids = claims.map((claim) => claim.serviceId?.toString() || '').filter(Boolean);
    return [...new Set(ids)];
  }, [claims]);

  const { services, isLoading } = useGetClaimServicesQuery({
    serviceIds: serviceIds,
    enabled: visible,
  });

  const getServiceByClaim = useCallback(
    (claim: LMNClaim) => {
      return services.find((s) => s.id === claim.serviceId?.toString());
    },
    [services]
  );

  return (
    <ModalWrapper
      title={`Expenses Justified by ${lmnName}`}
      visible={visible}
      onSetVisible={onSetVisible}
      testId="lmn-claims-modal"
      titleBoxStyle={{
        paddingBottom: 0,
      }}
    >
      <Box direction="column">
        <Inscription margin={{ top: 'spacing12' }} size="medium" textAlign="center">
          Claims listed here use this letter as their medical justification. Links are permanent.
        </Inscription>

        <Box margin={{ top: 'spacing24' }} background="module" round="moduleRound" pad="spacing24">
          <List>
            <List.Header>
              <List.Header.Item flex={2}></List.Header.Item>
              <List.Header.Item flex={1.2}>Amount</List.Header.Item>
              <List.Header.Item flex={0.8}>Date</List.Header.Item>
            </List.Header>

            {claims.map((claim) => (
              <LettersOfMedicalNecessityClaimListItem
                key={claim.claimId}
                claim={claim}
                service={getServiceByClaim(claim)}
                isLoading={isLoading}
              />
            ))}
          </List>
        </Box>

        <Box direction="row" justify="end" margin={{ top: 'spacing24' }}>
          <AppButton
            buttonType="primary"
            onClick={() => onSetVisible(false)}
            testId="lmn-claims-modal_done-button"
            width="140px"
          >
            Done
          </AppButton>
        </Box>
      </Box>
    </ModalWrapper>
  );
};
